import { createContext, useCallback, useContext, useState } from "react";

const MeasurementsContext = createContext();

export const useMeasurements = () => {
  const context = useContext(MeasurementsContext);
  if (!context) {
    throw new Error("UseMeasurements must be used within MeasurementsProvider");
  }
  return context;
};

export const MeasurementsProvider = ({ children }) => {
  const [originalMeasurements, setOriginalMeasurements] = useState({});
  const [currentMeasurements, setCurrentMeasurements] = useState({});
  const [targetMeasurements, setTargetMeasurements] = useState({});

  const clearMeasurements = useCallback(() => {
    setOriginalMeasurements({});
    setCurrentMeasurements({});
    setTargetMeasurements({});
  }, []);

  // Helper function to update measurements with logging
  const updateOriginalMeasurements = useCallback(measurements => {
    setOriginalMeasurements(measurements);
  }, []);

  const updateCurrentMeasurements = useCallback(measurements => {
    setCurrentMeasurements(measurements);
  }, []);

  const updateTargetMeasurements = useCallback(measurements => {
    setTargetMeasurements(measurements);
  }, []);

  // Bulk update function for handling multiple measurement types at once
  const updateAllMeasurements = useCallback(
    measurementsData => {
      if (measurementsData.original) {
        updateOriginalMeasurements(measurementsData.original);
      }
      if (measurementsData.current) {
        updateCurrentMeasurements(measurementsData.current);
      }
      if (measurementsData.target) {
        updateTargetMeasurements(measurementsData.target);
      }
    },
    [
      updateOriginalMeasurements,
      updateCurrentMeasurements,
      updateTargetMeasurements,
    ]
  );

  // Get measurement status
  const getMeasurementStatus = useCallback(() => {
    return {
      hasOriginal: Object.keys(originalMeasurements).length > 0,
      hasCurrent: Object.keys(currentMeasurements).length > 0,
      hasTarget: Object.keys(targetMeasurements).length > 0,
      totalMeasurements: {
        original: Object.keys(originalMeasurements).length,
        current: Object.keys(currentMeasurements).length,
        target: Object.keys(targetMeasurements).length,
      },
    };
  }, [originalMeasurements, currentMeasurements, targetMeasurements]);

  return (
    <MeasurementsContext.Provider
      value={{
        originalMeasurements,
        setOriginalMeasurements: updateOriginalMeasurements,
        currentMeasurements,
        setCurrentMeasurements: updateCurrentMeasurements,
        targetMeasurements,
        setTargetMeasurements: updateTargetMeasurements,
        clearMeasurements,
        updateAllMeasurements,
        getMeasurementStatus,
      }}
    >
      {children}
    </MeasurementsContext.Provider>
  );
};
